// src/components/GameLobby.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import FruitNinjaGame from './FruitNinjaGame';
import GameResult from './GameResult';

const GameLobby = ({ token, user: propUser }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('idle');
  const [roomId, setRoomId] = useState(null);
  const [opponent, setOpponent] = useState(null);
  const [opponentScore, setOpponentScore] = useState(0);
  const [myScore, setMyScore] = useState(0);
  const [gameResult, setGameResult] = useState(null);
  const socketRef = useRef(null);
  const gameActiveRef = useRef(false);
  const [user, setUser] = useState(propUser);

  useEffect(() => {
    if (!user) {
      const userStr = localStorage.getItem('memo_user');
      if (userStr) setUser(JSON.parse(userStr));
    }
  }, [user]);

  useEffect(() => {
    if (!token) return;
    const socket = io('/', { query: { token }, transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => console.log('WebSocket connected'));
    socket.on('connected', (data) => console.log('Server confirmed:', data));
    socket.on('match_success', (data) => {
      setRoomId(data.room_id);
      setOpponent({ id: data.opponent_id, username: data.opponent_username || `玩家${data.opponent_id}` });
      setStatus('playing');
      setMyScore(0);
      setOpponentScore(0);
    });
    socket.on('game_start', () => { gameActiveRef.current = true; });
    socket.on('opponent_action', (action) => {
      if (action.total_score !== undefined) setOpponentScore(action.total_score);
      else setOpponentScore(prev => prev + action.score);
    });
    socket.on('game_result', (result) => {
      setGameResult(result);
      setStatus('result');
      gameActiveRef.current = false;
    });
    socket.on('connect_error', (err) => console.error('Socket error:', err));

    return () => socket.disconnect();
  }, [token]);

  const startMatch = () => {
    setStatus('matching');
    socketRef.current.emit('match_start', { token });
  };

  const cancelMatch = () => {
    socketRef.current.emit('cancel_match', { token });
    setStatus('idle');
  };

  const handleGameAction = (type, score, combo) => {
    if (!gameActiveRef.current) return;
    if (type === 'slice') {
      setMyScore(prev => prev + score);
      socketRef.current.emit('game_action', { room_id: roomId, token, type: 'slice', score, combo });
    } else if (type === 'bomb') {
      setMyScore(prev => Math.max(0, prev + score));
      socketRef.current.emit('game_action', { room_id: roomId, token, type: 'bomb', score });
    }
  };

  const handleGameOver = async (finalScore) => {
    if (!gameActiveRef.current) return;
    gameActiveRef.current = false;
    socketRef.current.emit('game_over', { room_id: roomId, token, final_score: finalScore });
    try {
      const { gameAPI } = await import('../api');
      await gameAPI.updateHighScore(finalScore);
    } catch (err) { console.error('更新最高分失败', err); }
  };

  const handleExit = () => {
    if (window.confirm('退出游戏将判负，确定吗？')) {
      socketRef.current.emit('game_forfeit', { room_id: roomId, token });
      navigate('/game-center');
    }
  };

  const goBack = () => navigate('/game-center');

  if (status === 'playing') {
    return (
      <div style={styles.gameContainer}>
        <div style={styles.gameHeader}>
          <button onClick={handleExit} style={styles.exitButton}>🚪 退出游戏</button>
          <div style={styles.scoreBoard}><span>🍎 {myScore}</span><span>vs</span><span>🍉 {opponentScore}</span></div>
          <div style={styles.opponentInfo}>对手: {opponent?.username || '等待中'}</div>
        </div>
        <FruitNinjaGame onGameOver={handleGameOver} opponentScore={opponentScore} onAction={handleGameAction} isMyTurn={true} />
      </div>
    );
  }

  if (status === 'result') {
    return <GameResult result={gameResult} user={user} onPlayAgain={() => setStatus('idle')} />;
  }

  // 匹配大厅 UI
  return (
    <div style={styles.container}>
      <div style={styles.background} />
      <div style={styles.card}>
        <div style={styles.iconWrapper}>
          <span style={styles.fruitIcon}>🍉</span>
          <span style={styles.fruitIcon}>🍎</span>
          <span style={styles.fruitIcon}>🍊</span>
        </div>
        <h2 style={styles.title}>水果忍者 · 实时对战</h2>
        <p style={styles.subtitle}>匹配对手，60秒内切出高分！</p>

        {status === 'matching' ? (
          <div style={styles.matchingContainer}>
            <div style={styles.spinner} />
            <p style={styles.matchingText}>正在寻找对手...</p>
            <button onClick={cancelMatch} style={styles.cancelButton}>取消匹配</button>
          </div>
        ) : (
          <button onClick={startMatch} style={styles.startButton}>开始匹配</button>
        )}

        <button onClick={goBack} style={styles.backButton}>← 返回游戏中心</button>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    padding: '20px',
  },
  background: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)',
    zIndex: 0,
  },
  card: {
    position: 'relative',
    zIndex: 1,
    backgroundColor: 'rgba(255,255,255,0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: '30px',
    padding: '40px 30px',
    width: '100%',
    maxWidth: '450px',
    textAlign: 'center',
    boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
    border: '1px solid rgba(255,255,255,0.3)',
  },
  iconWrapper: { display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '20px' },
  fruitIcon: { fontSize: '48px', animation: 'float 2s ease-in-out infinite', display: 'inline-block' },
  title: { fontSize: '32px', fontWeight: 'bold', color: '#2c3e50', marginBottom: '10px' },
  subtitle: { fontSize: '16px', color: '#7f8c8d', marginBottom: '30px' },
  startButton: {
    backgroundColor: '#e67e22', color: 'white', border: 'none', padding: '14px 30px', fontSize: '18px',
    fontWeight: 'bold', borderRadius: '50px', cursor: 'pointer', width: '100%',
    transition: 'transform 0.2s, box-shadow 0.2s', boxShadow: '0 5px 0 #b45f1b', marginBottom: '20px',
  },
  backButton: { background: 'none', border: 'none', color: '#e67e22', fontSize: '16px', cursor: 'pointer', textDecoration: 'underline', marginTop: '10px' },
  matchingContainer: { textAlign: 'center' },
  spinner: { width: '50px', height: '50px', border: '4px solid #e0e0e0', borderTopColor: '#e67e22', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 20px' },
  matchingText: { fontSize: '18px', color: '#2c3e50', marginBottom: '20px' },
  cancelButton: { backgroundColor: '#e74c3c', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '50px', cursor: 'pointer', fontSize: '16px' },
  gameContainer: { minHeight: '100vh', background: 'linear-gradient(135deg, #1a2a3a, #0a0f1e)', padding: '20px' },
  gameHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' },
  exitButton: { background: 'rgba(255,255,255,0.2)', border: 'none', padding: '8px 16px', borderRadius: '20px', color: 'white', cursor: 'pointer', fontSize: '14px' },
  scoreBoard: { background: 'rgba(0,0,0,0.5)', padding: '8px 16px', borderRadius: '30px', color: 'white', fontSize: '20px', fontWeight: 'bold', display: 'flex', gap: '20px' },
  opponentInfo: { background: 'rgba(255,255,255,0.2)', padding: '8px 16px', borderRadius: '20px', color: 'white', fontSize: '14px' },
};

// 注入动画
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes float { 0% { transform: translateY(0px); } 50% { transform: translateY(-10px); } 100% { transform: translateY(0px); } }
`;
document.head.appendChild(styleSheet);

export default GameLobby;